import { PAApiServices } from "./pa-api-services";
import { PARegistry } from "../singletons/pa-registry";
import type { HolidaysTypes } from "date-holidays"
import type { CalendarWeek } from "src/app/_models/planning-assistant.interface"
import { PAOperation } from "./operation"
import type { WeekDay } from "./util";
import { PAUtil } from "./util"
import type { PATechnician } from "./technician"
import { TravelTechnicianDate } from "./travel-technician-date"
import type { Subscription } from "rxjs"
import { PATour } from "./tour";
import type { PALocation } from "./location";
import type { PAStore } from "./store";
import type { InsertionConfig } from "../tourplanner-menu/tour-timeline/insertion-config.interface";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import type { DraggableDestinationMarker } from "./draggable-marker";
import type { PAPlanningInstructions } from "./planning-instructions";
import { PANotifications } from "./pa-notifications";
import {
  NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES,
  NO_LOCATION_CHANGE_FOR_PROJECTS
} from "../../../_models/technician-tour.model";
import type { Absence } from "../../../_models/absence.interface";
import * as TourOptimizer from "./tour-optimizer/tour-optimizer";
import type { TourOptimizerContext, TourOptimizerOperation, TourOptimizerScoredRoute } from "./tour-optimizer/tour-optimizer.types";
import { TourDebugRecording, TourDebugStore } from "./tour-optimizer/tour-debug-recorder";
import type { TourDebugPlace, TourDebugRawOperation } from "./tour-optimizer/tour-debug.types";

interface NewTourConfig {
  force_new_tour?: boolean;
  insert_operations?: PAOperation[];
  delete_operations?: PAOperation[];
  custom_start_location?: {location: PALocation, marker: DraggableDestinationMarker} | 'remove'
  custom_end_location?: {location: PALocation, marker: DraggableDestinationMarker} | 'remove'
}

interface Insertion {
  operation: PAOperation;
  start_time_ms: number;
  tour_time_ms?: number;
  end_time_ms: number
}

function getRoutesLastFixedLocation(route: PAOperation[]) {
  // Rückwärts iterieren und beim ersten gültigen Treffer zurückgeben — ohne das sofortige
  // return würde die Schleife bis i=0 weiterlaufen und die ERSTE statt der letzten Location liefern
  for (let i = route.length - 1; i >= 0; i--) {
    const ticket = route[i].ticket
    const location = ticket.location
    const coords = location.coordinates
    const invalid_coordinates = NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES.find(c => c.lng == coords.longitude && c.lat == coords.latitude)
    const invalid_project = NO_LOCATION_CHANGE_FOR_PROJECTS.includes(ticket.project.id)
    if (!invalid_coordinates && !invalid_project) {
      return location
    }
  }
  return undefined;
}


export class PATechnicianDate extends FireUpdateOnChange<'new_tour'> {

  static defaultMaxWorkTime = 480
  static skipOperationUpdatesForMilliseconds = 1000 * 60 * 10 // 10min

  public loadingStatus: 'init' | 'loading' | 'loaded' = 'init'

  private _loadingTravelTechnicianDates = false
  public previousTravelTechnicianDate: TravelTechnicianDate
  public previousTravelTechnicianDateChangeSubscription: Subscription
  public travelTechnicianDate: TravelTechnicianDate
  public travelTechnicianDateChangeSubscription: Subscription

  public latestNewOperationUpdateTimestamp = 0
  /**
   * Aufzeichnung des laufenden Optimierungslaufs (siehe tour-debug-recorder.ts). Sie hängt bewusst
   * an der Instanz und nicht global, damit sich parallel rechnende Technikertage nicht vermischen.
   */
  private debugRecording: TourDebugRecording = null
  freeTourIndexes: number[] = []
  automaticLunchBreakCalculation = true
  lunchBreakAfterOperationNumber: number
  insertionConfigMap = new Map<number, InsertionConfig>()
  /** Vorgaben-Fingerabdruck des letzten Berechnungslaufs — siehe insertionConfigSignature() */
  private computedInsertionConfigSignature: string | null = null
  private _newTourConfig: NewTourConfig = {}
  public newTourTouched = false

  tour: PATour
  readonly isPastTechnicianDate: boolean
  readonly isTodaysTechnicianDate: boolean

  changedTourContainer: ChangedTourContainer

  public absences: Absence[] = []
  readonly noShift: boolean

  constructor(
    public technician: PATechnician,
    public date: string,
    public day: {
      week_day: WeekDay,
      utc_timestamp: number,
      local_timestamp: number,
      holidays: HolidaysTypes.Holiday[],
      date_string: string
    },
    public hawk_link: string,
    public id?: number
  ) {
    super()
    this.tour = this.generateNewEmptyTour()
    this.changedTourContainer = {
      tour: this.generateNewEmptyTour(),
      max_tour_minutes: 600,
      unfittingOperations: [],
      deletingOperations: []
    }
    const todays_day_timestamp = PARegistry.timeControl.dateToTimestamp(new Date(), true, true)
    this.isPastTechnicianDate = todays_day_timestamp > this.day.utc_timestamp
    this.isTodaysTechnicianDate = todays_day_timestamp == this.day.utc_timestamp

    this.noShift = !this.technician.hasShiftOnDay(this.day.utc_timestamp)
  }

  executeBeforeChange(): void {
    return
  }

  get newTourConfig(): NewTourConfig {
    return this._newTourConfig
  }

/*  selectTimelineTourForMap(skip_map_center?: boolean): void {
    PARegistry.tourPlannerControl.updateSelectedTourPlannerTours()
    if (!skip_map_center) PARegistry.tourPlannerControl.centerTourPlannerTourInMap(this)
  }*/

  async setTravelTechnicianDate(): Promise<void> {

    if (!this._loadingTravelTechnicianDates) {
      this._loadingTravelTechnicianDates = true
      let update_travel_status = false
      if (!this.travelTechnicianDate) {
        this.travelTechnicianDate = await this.getTravelTechnicianDate()
        this.travelTechnicianDateChangeSubscription = this.travelTechnicianDate.subscribeToChanges(() => {
          this.updateTourEndLocation()
          //this.updateNewTourOperationChanges()
          //this.changedTourContainer.tour.onTravelTechnicianDateChange()
        })
        update_travel_status = true
      }

      if (!this.previousTravelTechnicianDate) {
        const prev_technician_date = this.getPreviousTechnicianDate(true)
        if (prev_technician_date) {
          this.previousTravelTechnicianDate = await prev_technician_date.getTravelTechnicianDate()
          this.previousTravelTechnicianDateChangeSubscription = this.previousTravelTechnicianDate.subscribeToChanges(() => {
            this.updateTourStartLocation()
            //this.updateNewTourOperationChanges()
            //this.changedTourContainer.tour.onPreviousTravelTechnicianDateChange()
          })
          update_travel_status = true
        }
      }

      if (update_travel_status && this.tour) {
        this.updateTourStartLocation();
        //this.changedTourContainer.tour.updateStartLocation()
        this.updateTourEndLocation()
        //await this.changedTourContainer.tour.updateTravelStatus(true)
      }

      this._loadingTravelTechnicianDates = false
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return !this.travelTechnicianDate || this._loadingTravelTechnicianDates
      }
    )
  }

  public updateTourStartLocation() {
    const current_start_location = this.getStartLocation()
    if (current_start_location != this.tour.startLocation) {
      this.tour.startLocation = current_start_location
      PARegistry.tourPlannerControl.lastMainRouteLocationChangeTimestamp = Date.now()
    }
  }

  public updateTourEndLocation() {
    const current_end_location = this.getEndLocation()
    if (current_end_location != this.tour.endLocation) {
      this.tour.endLocation = current_end_location
      PARegistry.tourPlannerControl.lastMainRouteLocationChangeTimestamp = Date.now()
    }
  }


  public getStartLocation(): PALocation {
    const previous_technician_date = this.getPreviousTechnicianDate()
    if (this.previousTravelTechnicianDate?.isActive() && previous_technician_date) {
      return this.previousTravelTechnicianDate.location || this.technician.location
    } else {
      return this.technician.location
    }
  }

  public getEndLocation(): PALocation {
    if (this.travelTechnicianDate?.isActive()) {
      return this.travelTechnicianDate.location || this.technician.location
    } else {
      return this.technician.location
    }
  }

  public async getTravelTechnicianDate(): Promise<TravelTechnicianDate> {
    return await TravelTechnicianDate.getTravelTechnicianDate(this.technician.id, this.day.utc_timestamp)
  }

  public async waitUntilDataWasLoaded(): Promise<void> {
    if (this.loadingStatus == 'init') {
      await this.loadData()
    }

    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return this.loadingStatus == 'init' || this.loadingStatus == 'loading'
      }
    )
  }

  public getPublicHolidays(): HolidaysTypes.Holiday[] {
    return this.day.holidays.filter(holiday => holiday.type == 'public')
  }

  public getLocalHolidays(): HolidaysTypes.Holiday[] {
    return this.day.holidays.filter(holiday => holiday.type == 'observance')
  }

  public getCalendarWeek(): CalendarWeek {
    return PARegistry.timeControl.timestampToCalendarWeek(this.day.utc_timestamp)
  }

  public getPreviousTechnicianDate(init_if_not_existent?: boolean, load_data?: boolean): PATechnicianDate {
    return this.technician.getTechnicianDate(this.day.utc_timestamp - 24 * 60 * 60 * 1000, init_if_not_existent, load_data)
  }

  public getSubsequentTechnicianDate(init_if_not_existent?: boolean, load_data?: boolean): PATechnicianDate {
    return this.technician.getTechnicianDate(this.day.utc_timestamp + 24 * 60 * 60 * 1000, init_if_not_existent, load_data)
  }

  generateNewEmptyTour(config?: {custom_start_location?: PALocation, custom_end_location?: PALocation}): PATour {
    return new PATour(
      this,
      config?.custom_start_location || this.getStartLocation(),
      config?.custom_end_location || this.getEndLocation()
    )
  }

  public async loadData(): Promise<void> {
    if (this.loadingStatus != 'loading' && this.loadingStatus != 'loaded') {
      const utc_timestamp = this.day.utc_timestamp
      this.loadingStatus = 'loading'

      const day_operations = await PAApiServices.technicianDataLoader.getTechniciansOperationsForDayTimestamp(this.technician, utc_timestamp)
      if (this.loadingStatus == 'loading') {
        await this.executeOnLoaded()
        if (day_operations.length) {
          await PAOperation.insertOperationsInTechnicianDateAndStore(day_operations)
        }
        await this.tour.updateRoute()

      } else {
        if (day_operations.length) {
          await PARegistry.dataControl.updateOperations(day_operations)
        }
      }

    }
  }

  getWeekdayIdx(): number {
    return PARegistry.timeControl.weekDays.indexOf(this.day.week_day)
  }

  hasOperationWithIdInChangedTour(op_id: number): boolean {
    const ctc = this.changedTourContainer
    return !!ctc.tour.operations.concat(ctc.unfittingOperations).find(op => op.id == op_id)
  }

  async updateNewTour(config?: NewTourConfig): Promise<void> {
    const insertion_operations = config?.insert_operations || []
    const deletion_operations = config?.delete_operations || []
    const deletion_operation_ids = deletion_operations.map(op => op.id)
    this.updateInsertionConfigMap(insertion_operations)

    const possible_tdc = PARegistry.tourPlannerControl.tourPlannerOverviewTechnicianDateCollections.find(tdc => tdc.technicianDates.includes(this))
    const auto_optimize_target_location = possible_tdc ? possible_tdc.autoOptimizeTravelDestinationsForDayTimestamps.includes(this.day.utc_timestamp) : false
    const tour_will_have_operations = !!this.tour.operations.concat(insertion_operations).filter(o => !deletion_operations.find(delete_op => delete_op.id == o.id)).length
    const has_travel_start_location = (config?.custom_start_location != 'remove' && config?.custom_start_location?.location) || (this.getStartLocation() != this.technician.location)
    const set_end_location_to_last_operations_location = has_travel_start_location
      && tour_will_have_operations
      && auto_optimize_target_location

    const use_start_location = config?.custom_start_location ? (config.custom_start_location == 'remove' ? this.technician.location : config.custom_start_location.location) : this.getStartLocation()
    let use_end_location: PALocation
    if (set_end_location_to_last_operations_location) {
      use_end_location = null
    } else {
      use_end_location = config?.custom_end_location ? (config.custom_end_location == 'remove' ? this.technician.location : config.custom_end_location.location) : this.getEndLocation()
    }

    const all_operations_used_in_new_tour = insertion_operations.concat(this.tour.operations.filter(op => !deletion_operation_ids.includes(op.id)))
    const { equal_operations } = this.tour.compareRoute(all_operations_used_in_new_tour)
    const start_location_matches = this.tour.startLocation == use_start_location
    const end_location_matches = (set_end_location_to_last_operations_location && this.tour.operations.length) ? (this.tour.endLocation ==  this.tour.operations[this.tour.operations.length - 1].ticket.location) : (this.tour.endLocation == use_end_location)

    const update_ts = this.latestNewOperationUpdateTimestamp = Date.now()
    this.startDebugRecording(update_ts, use_start_location, use_end_location)
    // try/finally stellt sicher, dass das Throbber-Flag auch bei Fehlern in der
    // Routenberechnung zurückgesetzt wird — sonst hängt „Berechne optimierte Route" dauerhaft
    try {
      let found_valid_route: OptimizedRoute
      let no_valid_route_found = false
      let used_fixed_lateness_ms = 0
      const lunch_break_changed =!this.automaticLunchBreakCalculation && this.tour.lunchBreak && this.tour.lunchBreak.after_operation_idx != this.lunchBreakAfterOperationNumber - 1

      const insertion_config_signature = this.insertionConfigSignature(all_operations_used_in_new_tour)
      // Beim ersten Lauf gibt es noch keinen Vergleichsstand — dann zählt die Vorgabe nicht als
      // Änderung, sonst würde schon das Öffnen des Planers jede Tour einmal durchrechnen
      const insertion_config_changed = this.computedInsertionConfigSignature != null
        && insertion_config_signature != this.computedInsertionConfigSignature

      this.newTourTouched = !equal_operations || this.tourOperationsOrderViolateInsertionConfig(this.tour.operations) || insertion_config_changed || !start_location_matches || !end_location_matches || lunch_break_changed

      // Ohne Neuberechnung passt die bestehende Tour bereits zu den Vorgaben — Stand festhalten,
      // damit eine spätere Lockerung als Änderung erkannt wird. Bei einer Neuberechnung wird der
      // Stand erst nach getaner Arbeit übernommen, sonst verschluckte ein überholter Lauf die
      // Änderung für den nachfolgenden.
      if (!this.newTourTouched) this.computedInsertionConfigSignature = insertion_config_signature

      if (this.newTourTouched) {

        const scored_routes = await this.calculatePossibleRoutes(update_ts, {start_location: use_start_location, end_location: use_end_location, operations: all_operations_used_in_new_tour})
        if (update_ts != this.latestNewOperationUpdateTimestamp) return

        const { optimized_route, used_scored_route } = await this.getOptimizedRoute(scored_routes, {start_location: use_start_location, end_location: use_end_location})
        if (update_ts != this.latestNewOperationUpdateTimestamp) return

        found_valid_route = optimized_route

        if (!found_valid_route) {
          no_valid_route_found = true
          // Keine einzige machbare Route (z. B. Fixtermin zeitlich unerreichbar): Die neu
          // einzufügenden Aufträge als „nicht einfügbar" markieren, die bestehende Tour
          // unverändert weiterzeigen und über die Fehler-Snackbar (unten) informieren
          const unfitting_operations_before = this.changedTourContainer.unfittingOperations
          const tour_operation_ids = this.tour.operations.map(op => op.id)
          this.changedTourContainer.unfittingOperations = insertion_operations.filter(op => !tour_operation_ids.includes(op.id)).map(o => o.clone());
          this.changedTourContainer.deletingOperations = deletion_operations;
          [...new Set(unfitting_operations_before.concat(this.changedTourContainer.unfittingOperations))].map(op => op.getStore().fireUpdateManually())
          this.changedTourContainer.unfittingOperations.concat(this.changedTourContainer.deletingOperations).map(op => {
            const config = this.insertionConfigMap.get(op.id)
            if (config) config.tour_index = -1
          })

          found_valid_route = {
            errors: ['Keine gültige Route gefunden — Fixtermin(e) oder Beschränkungen sind nicht erfüllbar; der Auftrag konnte nicht eingeplant werden.'],
            insertions: this.tour.operations.filter(op => !deletion_operation_ids.includes(op.id)).map(op =>
              {
                return {
                  operation: op,
                  start_time_ms: op.calculateOperationDayStartMilliseconds(),
                  end_time_ms: op.calculateOperationDayEndMilliseconds(),
                }
              }
            ),
            lunch_break: this.tour.lunchBreak
          }
          // Diese insertions enthalten die gestarteten Aufträge bereits → Range ist schon absolut
          this.freeTourIndexes = PAUtil.range(0, found_valid_route.insertions.length - 1)
        }

        if (used_scored_route) {
          used_fixed_lateness_ms = used_scored_route.fixed_lateness_ms || 0
          const unfitting_operations_before = this.changedTourContainer.unfittingOperations
          this.changedTourContainer.unfittingOperations = used_scored_route.removed_operations.map(o => o.clone());
          this.changedTourContainer.deletingOperations = deletion_operations;
          [...new Set(unfitting_operations_before.concat(this.changedTourContainer.unfittingOperations))].map(op => op.getStore().fireUpdateManually())
          this.changedTourContainer.unfittingOperations.concat(this.changedTourContainer.deletingOperations).map(op => {
            const config = this.insertionConfigMap.get(op.id)
            if (config) config.tour_index = -1
          })

        }

        if (used_scored_route && found_valid_route) {
          // Absolute Index-Basis: Bereits gestartete Aufträge stehen fest am Tour-Anfang und
          // fehlen in den insertions — die wählbaren Tour-Nummern müssen sie trotzdem mitzählen,
          // damit sie zu den absoluten tour_index-Werten und den Timeline-Badges passen
          const started_operation_ids = new Set(this.tour.operations.concat(insertion_operations).filter(op => op.date_travel_start).map(op => op.id))
          this.freeTourIndexes = PAUtil.range(0, started_operation_ids.size + found_valid_route.insertions.length - 1)
        }

      } else {
        // COPY OLD TOUR
        found_valid_route = {
          errors: [],
          insertions: this.tour.operations.map(op =>
            {
              return {
                operation: op,
                start_time_ms: op.calculateOperationDayStartMilliseconds(),
                end_time_ms: op.calculateOperationDayEndMilliseconds(),
              }
            }
          ),
          lunch_break: this.tour.lunchBreak
        }
        this.changedTourContainer.unfittingOperations = []
        this.changedTourContainer.deletingOperations = [];
        this.freeTourIndexes = PAUtil.range(0, this.tour.operations.length - 1)
      }

      const new_tour = this.generateNewEmptyTour({ custom_start_location: use_start_location, custom_end_location: use_end_location })
      found_valid_route.lunch_break ? new_tour.addLunchBreak(found_valid_route.lunch_break.after_operation_idx, found_valid_route.lunch_break.duration_minutes) : {}

      const operations = this.tour.operations.concat(insertion_operations)

      const cloned_fixed_operations = operations.filter(operation => operation.date_travel_start).map(op => op.clone())
      const cloned_insert_operations = found_valid_route.insertions.map(i => i.operation.clone())
      for (const operation of cloned_fixed_operations.concat(cloned_insert_operations)) {
        operation.tour = new_tour
        operation.user_ids = [new_tour.technicianDate.technician.id]
      }
      for (const operation of cloned_insert_operations) {
        const start_time_ms = found_valid_route.insertions.find(i => i.operation.id == operation.id).start_time_ms
        operation.scheduledStartDayMs = start_time_ms

        // Fixtermine behalten ihre vereinbarte Uhrzeit. Wird ein Fixtermin verspätet erreicht,
        // liefert die Berechnung die Ankunftszeit — würde die hier zurückgeschrieben, wäre der
        // Kundentermin verschoben und diese Verschiebung anschließend im Speichern-Menü als
        // Änderung angeboten. Der Tagesablauf rechnet über scheduledStartDayMs weiter.
        if (operation.date_fixed) continue

        const date_time_ms = PARegistry.timeControl.dateToTimestamp(new Date(new_tour.technicianDate.day.utc_timestamp), true, false) + start_time_ms
        operation.operation_date = PARegistry.timeControl.formatDate(new Date(date_time_ms))
      }

      const insert_operations = cloned_fixed_operations.concat(cloned_insert_operations)

      await new_tour.insertOperations(
        insert_operations,
        {
          wait_for_all_queued_updates: true,
          set_end_location_to_last_operation: set_end_location_to_last_operations_location
        }
      )

      // Erst jetzt gilt die Tour als zu den Vorgaben passend berechnet
      this.computedInsertionConfigSignature = insertion_config_signature

      const old_start_location = this.changedTourContainer.tour.route.start_location
      const old_end_location = this.changedTourContainer.tour.route.end_location
      const start_or_end_location_changed = old_start_location != new_tour.startLocation || old_end_location != new_tour.endLocation

      if (set_end_location_to_last_operations_location && new_tour.operations.length) {
        let marker: DraggableDestinationMarker = config?.custom_end_location != 'remove' ? config?.custom_end_location?.marker : null
        if (!marker) {
          const possible_tdc = PARegistry.tourPlannerControl.tourPlannerOverviewTechnicianDateCollections.find(tdc => tdc.technicianDates.includes(this))
          if (possible_tdc) {
            possible_tdc.activateTravelTechnicianDateTimestamp(this.day.utc_timestamp)
            marker = possible_tdc.getMarkerForTimestampIfActive(this.day.utc_timestamp)
          }
        }
        if (marker) {
          marker.coordinates = new_tour.endLocation.coordinates
        }
      }

      const { equal_operation_times } = this.changedTourContainer.tour.compareRoute(new_tour.operations)
      if (config?.force_new_tour || (!equal_operation_times) || start_or_end_location_changed) {
        this.changedTourContainer.tour = new_tour
        this.fireUpdateManually('new_tour')
        PARegistry.tourPlannerControl.updateTechnicianToursInPlanningProcess()
      }

      // Aufzeichnung abschließen, bevor die Meldung erscheint — der Knopf in der Meldung öffnet sie
      this.finishDebugRecording(update_ts, !no_valid_route_found, found_valid_route, used_fixed_lateness_ms)

      if (found_valid_route.errors.length) {
        PANotifications.open(
          `Optimierung: \n Achtung! Keine Route für ${this.technician.getFullName()} - ${this.day.date_string} möglich, die alle Beschränkungen erfüllt: \n ${found_valid_route.errors.join(' \n ')}`,
          'Ok',
          {
            type: 'error',
            position: 'top',
            extra_actions: [{
              label: 'Debug-Infos',
              keep_open: true,
              handler: () => TourDebugStore.openRequests.next(TourDebugStore.key(this.technician.id, this.day.utc_timestamp))
            }]
          }
        )
      }

    } finally {
      // Nur zurücksetzen, wenn kein neuerer Lauf gestartet wurde — dessen finally übernimmt sonst
      if (update_ts == this.latestNewOperationUpdateTimestamp) {
        this.latestNewOperationUpdateTimestamp = null
      }
    }
  }

  private tourOperationsOrderViolateInsertionConfig(ordered_tour_operations: PAOperation[]): boolean {
    for (const operation of ordered_tour_operations) {
      const idx = ordered_tour_operations.indexOf(operation)
      const config = this.insertionConfigMap.get(operation.id)
      if (config.tour_index >= 0 && idx != config.tour_index) {
        return true
      }
    }
    return false
  }

  /**
   * Fingerabdruck der Positions- und Fixierungs-Vorgaben, mit denen die aktuelle Tour berechnet
   * wurde. Nötig, weil die Verletzungsprüfung nur *verschärfte* Vorgaben erkennt: Wird eine
   * festgesetzte Position wieder auf „opt" gestellt, verletzt die bestehende Reihenfolge nichts
   * mehr — ohne diesen Vergleich bliebe die Tour dann einfach so stehen, wie sie von Hand
   * angeordnet wurde, statt die neu gewonnene Freiheit zu nutzen.
   */
  private insertionConfigSignature(operations: PAOperation[]): string {
    return operations
      .map(operation => {
        const config = this.insertionConfigMap.get(operation.id)
        return `${operation.id}:${config ? config.tour_index : -1}:${config ? (config.removable ? 1 : 0) : 0}`
      })
      .sort()
      .join('|')
  }

  /**
   * HAUPTFUNKTION: Berechnet alle möglichen Routen-Permutationen und bewertet sie.
   *
   * ABLAUF:
   * 1. Aufträge kategorisieren (bereits gestartet, mit/ohne Ortswechsel, Fixtermine)
   * 2. Stores mit Ortswechsel permutieren (Fixtermine haben Vorrang beim 6er-Limit)
   * 3. Für jede Permutation: Fixtermine müssen in chronologischer Reihenfolge sein
   * 4. findTimeConstrainedPartialRoute prüft ob Route zeitlich machbar ist
   * 5. Gültige Routen bewerten (Distanz/Dauer) und zurückgeben
   *
   * RÜCKGABE: Array von ScoredRoute - jede enthält eine mögliche Route mit Bewertung
   */
  async calculatePossibleRoutes(update_ts: number, config: {start_location: PALocation, end_location: PALocation, operations: PAOperation[]}): Promise<ScoredRoute[]> {

    const operations = config.operations || []
    const use_start_location = config.start_location
    const use_end_location = config.end_location
    const permutation_input_limit = 6  // Max 6 Stores permutieren (6! = 720 Kombinationen)

    const store_operations_map = new Map<PAStore, PAOperation[]>()

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 1: Aufträge in Kategorien aufteilen
    // ═══════════════════════════════════════════════════════════════════════════

    // 1a) Bereits gestartete Aufträge - haben feste Position (Techniker ist schon unterwegs/vor Ort)
    //     Sortiert nach Startzeit, da die Reihenfolge feststeht
    const ordered_already_started_operations = operations.filter(operation => operation.date_travel_start).sort((operation_a, operation_b) => Date.parse(operation_a.date_travel_start) < Date.parse(operation_b.date_travel_start) ? -1 : 1)

    // 1b) Noch nicht gestartete Aufträge - können optimiert werden
    const not_started_operations = operations.filter(operation => !operation.date_travel_start)

    // 1b2) Heute bereits verstrichene Fixtermine (mit Ortswechsel) sind faktisch Vergangenheit:
    //      Sie stehen fest am Tour-Anfang (nach den gestarteten Aufträgen), werden nicht permutiert
    //      und in zeitlicher Reihenfolge eingeordnet — sonst laufen Routen-Reihenfolge (Basis der
    //      Fahrzeitberechnung) und Zeit-Reihenfolge (Anzeige) auseinander
    const ts_now_for_passed_check = Date.now()
    const day_is_today = this.day.utc_timestamp < ts_now_for_passed_check && ts_now_for_passed_check < this.day.utc_timestamp + 24 * 60 * 60 * 1000
    const now_day_ms_for_passed_check = day_is_today ? ts_now_for_passed_check - PARegistry.timeControl.dateToTimestamp(new Date(), true, false) : null
    const isPassedTodayFixedOperation = (op: PAOperation): boolean =>
      now_day_ms_for_passed_check != null && op.date_fixed && PARegistry.timeControl.dateTimeToDayMinutes(op.operation_date) * 60 * 1000 < now_day_ms_for_passed_check
    const ordered_passed_fixed_operations = not_started_operations
      .filter(op => op.ticket.triggersLocationChange() && isPassedTodayFixedOperation(op))
      .sort((op_a, op_b) => Date.parse(op_a.operation_date) - Date.parse(op_b.operation_date))

    // 1c) Aufträge mit festem Tour-Index (User hat manuell Position festgelegt)
    //     Diese werden NICHT permutiert, sondern an ihrer festen Position eingefügt
    const not_started_operations_with_set_tour_idx = not_started_operations.filter(o => !ordered_passed_fixed_operations.includes(o) && this.insertionConfigMap.has(o.id) && this.insertionConfigMap.get(o.id).tour_index >= 0)
    const ordered_operations_with_tour_idx = not_started_operations_with_set_tour_idx.sort((o1, o2) => {
      return this.insertionConfigMap.get(o1.id).tour_index < this.insertionConfigMap.get(o2.id).tour_index ? -1 : 1
    })

    // 1d) Aufträge die optimiert werden sollen (kein fester Tour-Index)
    const not_started_operations_to_optimize = not_started_operations.filter(o => !ordered_passed_fixed_operations.includes(o) && (!this.insertionConfigMap.has(o.id) || this.insertionConfigMap.get(o.id).tour_index == -1))

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 2: Aufträge nach Ortswechsel, Fixtermin und Tages-SLA kategorisieren
    // ═══════════════════════════════════════════════════════════════════════════

    // Hilfsfunktion: Prüft ob ein Auftrag ein Tages-SLA hat (SLA am gleichen Tag wie die geplante Tour)
    const hasSameDaySLA = (op: PAOperation): boolean => {
      if (!op.ticket.datesla) return false
      const sla_day_timestamp = PARegistry.timeControl.dateToTimestamp(new Date(op.ticket.datesla), true, true)
      return sla_day_timestamp === this.day.utc_timestamp
    }

    // Hilfsfunktion: Gibt die Prioritäts-Uhrzeit eines Auftrags zurück (für Sortierung)
    // Fixtermine: operation_date, Tages-SLA: datesla
    const getPriorityTime = (op: PAOperation): number => {
      if (op.date_fixed) {
        return new Date(op.operation_date).getTime()
      } else if (hasSameDaySLA(op)) {
        return new Date(op.ticket.datesla).getTime()
      }
      return Infinity  // Normale Aufträge haben keine Prioritäts-Zeit
    }

    // 2a) Fixtermine MIT Ortswechsel
    const fixed_date_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => op.date_fixed && op.ticket.triggersLocationChange()
    )

    // 2b) Tages-SLA Aufträge MIT Ortswechsel (keine Fixtermine)
    //     SLA-Datum ist am gleichen Tag wie die geplante Tour
    const same_day_sla_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && op.ticket.triggersLocationChange() && hasSameDaySLA(op)
    )

    // 2c) Prioritäts-Aufträge = Fixtermine + Tages-SLA (beide gleichwertig, nach Uhrzeit sortiert)
    const priority_operations_with_location_change = [
      ...fixed_date_operations_with_location_change,
      ...same_day_sla_operations_with_location_change
    ].sort((op_a, op_b) => getPriorityTime(op_a) - getPriorityTime(op_b))

    // 2d) Fixtermine OHNE Ortswechsel → werden nicht permutiert (kein Einfluss auf Fahrzeit)
    const fixed_date_operations_without_location_change = not_started_operations_to_optimize.filter(
      op => op.date_fixed && !op.ticket.triggersLocationChange()
    )

    // 2e) Normale Aufträge MIT Ortswechsel (keine Fixtermine, kein Tages-SLA) → werden permutiert
    const normal_operations_with_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && op.ticket.triggersLocationChange() && !hasSameDaySLA(op)
    )

    // 2f) Normale Aufträge OHNE Ortswechsel → werden nicht permutiert
    const normal_operations_without_location_change = not_started_operations_to_optimize.filter(
      op => !op.date_fixed && !op.ticket.triggersLocationChange()
    )

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 3: Store-zu-Operations Mapping erstellen
    // ═══════════════════════════════════════════════════════════════════════════
    // Mehrere Aufträge am gleichen Store werden zusammen behandelt (gleicher Ort)

    // Alle Aufträge mit Ortswechsel (Prioritäts-Aufträge + normale)
    const all_operations_with_location_change = [...priority_operations_with_location_change, ...normal_operations_with_location_change]
    for (const operation of all_operations_with_location_change) {
      const operations_store = operation.getStore()
      if (!store_operations_map.has(operations_store)) {
        store_operations_map.set(operations_store, [operation])
      } else {
        store_operations_map.get(operations_store).push(operation)
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 4: Stores für Permutation vorbereiten
    // ═══════════════════════════════════════════════════════════════════════════
    // Prioritäts-Aufträge (Fixtermine + Tages-SLA) haben Vorrang beim 6er-Limit

    // 4a) Prioritäts-Stores nach Zeit sortieren (früheste zuerst)
    //     Bei mehr als 6 werden die späteren abgelehnt
    const priority_stores_sorted = [...new Set(priority_operations_with_location_change.map(op => op.getStore()))]

    // 4b) Stores mit normalen Aufträgen
    let normal_stores = [...new Set(normal_operations_with_location_change.map(op => op.getStore()))]
    // Stores die schon bei Prioritäts-Aufträgen dabei sind, nicht doppelt zählen
    normal_stores = normal_stores.filter(store => !priority_stores_sorted.includes(store))

    // 4c) Limit anwenden: Prioritäts-Aufträge zuerst, dann normale Stores auffüllen
    let stores_to_permute: PAStore[] = []
    let stores_not_to_permute: PAStore[] = []
    let rejected_priority_operations: PAOperation[] = []  // Prioritäts-Aufträge die nicht eingeplant werden können

    if (priority_stores_sorted.length <= permutation_input_limit) {
      // Fall 1: Alle Prioritäts-Aufträge passen ins Limit
      stores_to_permute = [...priority_stores_sorted]
      // Restliche Plätze mit normalen Stores auffüllen
      const remaining_slots = permutation_input_limit - priority_stores_sorted.length
      stores_to_permute = stores_to_permute.concat(normal_stores.slice(0, remaining_slots))
      stores_not_to_permute = normal_stores.slice(remaining_slots)
    } else {
      // Fall 2: Mehr Prioritäts-Stores als Limit erlaubt
      // → Nur die ersten 6 (nach Zeit) permutieren, Rest als nicht einfügbar markieren
      stores_to_permute = priority_stores_sorted.slice(0, permutation_input_limit)
      const rejected_stores = priority_stores_sorted.slice(permutation_input_limit)
      stores_not_to_permute = [...normal_stores]  // Normale Stores werden nicht permutiert

      // Alle Prioritäts-Aufträge der abgelehnten Stores sammeln
      for (const rejected_store of rejected_stores) {
        const store_priority_ops = priority_operations_with_location_change.filter(op => op.getStore() === rejected_store)
        rejected_priority_operations = rejected_priority_operations.concat(store_priority_ops)
      }

      console.warn(`Mehr als ${permutation_input_limit} Prioritäts-Stores (${priority_stores_sorted.length}). ` +
        `${rejected_priority_operations.length} Fixtermin(e)/SLA-Auftrag/Aufträge können nicht eingeplant werden.`)
    }

    // 4d) Abgelehnte Prioritäts-Aufträge aus der Liste entfernen
    const accepted_priority_operations = priority_operations_with_location_change.filter(
      op => !rejected_priority_operations.includes(op)
    )

    const storeLabel = (store: PAStore): string => store.address_company || store.address || `Store ${store.id}`
    this.debugRecording?.setCategories({
      total: operations.length,
      already_started: ordered_already_started_operations.length,
      passed_fixed: ordered_passed_fixed_operations.length,
      pinned_tour_index: ordered_operations_with_tour_idx.length,
      fixed_with_location_change: fixed_date_operations_with_location_change.length,
      same_day_sla_with_location_change: same_day_sla_operations_with_location_change.length,
      normal_with_location_change: normal_operations_with_location_change.length,
      fixed_without_location_change: fixed_date_operations_without_location_change.length,
      normal_without_location_change: normal_operations_without_location_change.length
    })
    this.debugRecording?.setStores({
      permutation_limit: permutation_input_limit,
      permuted: stores_to_permute.map(storeLabel),
      not_permuted: stores_not_to_permute.map(storeLabel),
      rejected_priority_operation_ids: rejected_priority_operations.map(op => op.id)
    })

    // 4e) Nur akzeptierte FIXTERMINE für Reihenfolge-Validierung (SLA-Aufträge sind flexibler)
    const accepted_fixed_date_operations = accepted_priority_operations.filter(op => op.date_fixed)

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 5: Distance-Matrix vorladen (Performance-Optimierung)
    // ═══════════════════════════════════════════════════════════════════════════

    // Letzte bekannte Location vor den Permutationen (für Distanzberechnung)
    const last_fixed_location_before_permutations = getRoutesLastFixedLocation(ordered_already_started_operations.concat(ordered_passed_fixed_operations)) || use_start_location

    // Nur Locations der AKZEPTIERTEN Aufträge für die Matrix sammeln (MapBox-Requests sparen)
    const accepted_operations_with_location_change = all_operations_with_location_change.filter(
      op => !rejected_priority_operations.includes(op)
    )
    const all_locations_for_matrix = [...new Set(accepted_operations_with_location_change.map(op => op.ticket.location))]
    if (all_locations_for_matrix.length >= 1) {
      await PARegistry.dataControl.distanceCalculator.preloadLocationDistanceMatrix(
        [last_fixed_location_before_permutations].concat(all_locations_for_matrix),
        all_locations_for_matrix.concat(use_end_location ? [use_end_location] : []),
        'min_credits'
      )
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 6: Alle Store-Permutationen generieren und bewerten
    // ═══════════════════════════════════════════════════════════════════════════

    const store_permutations = PAUtil.permutator<PAStore>(stores_to_permute)
    const scored_permutations: ScoredRoute[] = []
    let lowest_operation_remove_count = -1  // Tracking: Beste Route mit wenigsten entfernten Ops

    // Permutationen in Chunks verarbeiten (für bessere Performance und Abbruch-Prüfung)
    const permutation_chunks = PAUtil.chunkArray<PAStore[]>(store_permutations, 32)
    let invalid_permutation_count = 0

    for (const stores_chunk of permutation_chunks) {
      console.log(`starting chunk ${permutation_chunks.indexOf(stores_chunk)}`)
      // Abbruch wenn zwischenzeitlich neue Berechnung gestartet wurde
      if (update_ts != this.latestNewOperationUpdateTimestamp) return

      await Promise.all(stores_chunk.map(async store_permutation => {
        this.debugRecording?.countPermutation()

        // 6a) Prüfen ob Fixtermine in dieser Permutation in chronologischer Reihenfolge sind
        //     Nur die akzeptierten Fixtermine prüfen (nicht die abgelehnten)
        if (!this.isValidFixedDateOrder(store_permutation, accepted_fixed_date_operations, store_operations_map)) {
          invalid_permutation_count += 1
          this.debugRecording?.countRejection('fixtermin_reihenfolge')
          return  // Diese Permutation überspringen
        }

        // 6b) Route aus der Store-Permutation aufbauen
        let ordered_operations: PAOperation[] = []
        for (const store of store_permutation.concat(stores_not_to_permute)) {
          const store_operations = store_operations_map.get(store)
          if (store_operations) {
            ordered_operations = ordered_operations.concat(store_operations)
          }
        }

        // 6c) Bereits gestartete Aufträge und verstrichene Fixtermine VOR die permutierten setzen
        const route = ordered_already_started_operations.concat(ordered_passed_fixed_operations).concat(ordered_operations)

        // 6d) Aufträge mit festem Tour-Index an ihrer Position einfügen
        for (const operation of ordered_operations_with_tour_idx) {
          const idx = this.insertionConfigMap.get(operation.id).tour_index
          route.splice(idx, 0, operation)
        }

        // 6e) Prüfen ob Route ins Zeitbudget passt (und ggf. Aufträge entfernen)
        const valid_partial_route = await this.findTimeConstrainedPartialRoute({
          route: route,
          start_location: use_start_location,
          end_location: use_end_location,
          extra_operations_without_location_change: normal_operations_without_location_change,
          fixed_date_operations_without_location_change: fixed_date_operations_without_location_change
        }, this.changedTourContainer.max_tour_minutes * 60 * 1000, lowest_operation_remove_count)

        // 6f) Wenn gültige Route gefunden: zur Liste hinzufügen
        if (valid_partial_route) {
          // Abgelehnte Fixtermine zu removed_operations hinzufügen
          const all_removed_operations = [...valid_partial_route.removed_operations, ...rejected_priority_operations]
          const removed_operation_count = all_removed_operations.length

          // Nur Routen behalten die mindestens so gut sind wie die beste bisherige
          if (lowest_operation_remove_count == -1 || removed_operation_count <= lowest_operation_remove_count) {
            lowest_operation_remove_count = all_removed_operations.length
            scored_permutations.push({
              route: valid_partial_route.partial_route,
              score: null,  // Wird später berechnet
              removed_operations: all_removed_operations,
              sorted_extra_operations_with_appointment_date: valid_partial_route.fixed_date_operations_without_location_change,
              fixed_lateness_ms: valid_partial_route.fixed_lateness_ms
            })
          } else {
            invalid_permutation_count += 1
            this.debugRecording?.countRejection('schlechter_als_beste')
          }
        } else {
          invalid_permutation_count += 1
          this.debugRecording?.countRejection('zu_viele_entfernte_auftraege')
        }
      }))
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // SCHRITT 7: Gültige Routen bewerten (Score berechnen)
    // ═══════════════════════════════════════════════════════════════════════════

    if (update_ts != this.latestNewOperationUpdateTimestamp) return
    await Promise.all(scored_permutations.map(async store_permutation => {
      // Score = Gesamtdistanz und Gesamtdauer der Route
      store_permutation.score = await this.scoreRoute(store_permutation.route, use_start_location, use_end_location)
    }))

    for (const scored_permutation of scored_permutations) {
      this.debugRecording?.addCandidate({
        operation_ids: scored_permutation.route.map(op => op.id),
        removed_operation_ids: scored_permutation.removed_operations.map(op => op.id),
        fixed_lateness_ms: scored_permutation.fixed_lateness_ms || 0,
        distance: scored_permutation.score ? scored_permutation.score.distance : null,
        duration: scored_permutation.score ? scored_permutation.score.duration : null
      })
    }

    return scored_permutations
  }

  /**
   * Prüft ob die Fixtermine in einer Store-Permutation in chronologischer Reihenfolge sind.
   * Fixtermine müssen in der Route in der gleichen Reihenfolge erscheinen wie ihre Uhrzeiten.
   */
  private isValidFixedDateOrder(
    store_permutation: PAStore[],
    sorted_fixed_date_operations: PAOperation[],
    store_operations_map: Map<PAStore, PAOperation[]>
  ): boolean {
    if (sorted_fixed_date_operations.length <= 1) return true  // 0 oder 1 Fixtermin → immer gültig

    // Finde die Position jedes Fixtermins in der Permutation
    const fixed_date_positions: {operation: PAOperation, position: number}[] = []

    for (const fixed_op of sorted_fixed_date_operations) {
      const op_store = fixed_op.getStore()
      let store_position = store_permutation.indexOf(op_store)
      if (store_position === -1) {
        // Store nicht in Permutation (wurde ans Ende gehängt) → Position = Infinity
        store_position = Infinity
      }
      fixed_date_positions.push({operation: fixed_op, position: store_position})
    }

    // Prüfen ob die Positionen aufsteigend sind (entsprechend der zeitlichen Sortierung)
    for (let i = 1; i < fixed_date_positions.length; i++) {
      if (fixed_date_positions[i].position < fixed_date_positions[i - 1].position) {
        return false  // Fixtermin i kommt VOR Fixtermin i-1, aber zeitlich ist i NACH i-1
      }
    }

    return true
  }


  // ═══════════════════════════════════════════════════════════════════════════
  // Adapter zum puren Tourenoptimierungs-Kern (classes/tour-optimizer/)
  // Wandelt PAOperation/PALocation + Singleton-Zustand in Plain-Data um; die
  // eigentliche Zeitrechnung lebt im Kern und ist dort isoliert unit-getestet.
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // Debug-Aufzeichnung (siehe tour-optimizer/tour-debug-recorder.ts)
  // Läuft bei jeder Optimierung mit, damit ein Dispatcher einen Fall auch dann weiterreichen kann,
  // wenn keine Fehlermeldung kam. Fehler in der Aufzeichnung dürfen die Planung nie stören —
  // deshalb sind Start und Abschluss abgesichert.
  // ═══════════════════════════════════════════════════════════════════════════

  private debugPlace(location: PALocation): TourDebugPlace {
    return {
      location_id: this.optimizerLocationKey(location),
      city: location.city || null,
      zip_code: location.zip_code || null,
      street: location.street || null
    }
  }

  private startDebugRecording(run_timestamp: number, start_location: PALocation, end_location: PALocation | null): void {
    try {
      const ctx = this.buildOptimizerContext()
      const recording = new TourDebugRecording({
        recorded_at: new Date(run_timestamp).toISOString(),
        recorded_at_ms: run_timestamp,
        technician_id: this.technician.id,
        technician_name: this.technician.getFullName(),
        day_timestamp: this.day.utc_timestamp,
        day_date: this.day.date_string,
        day_is_today: this.isTodaysTechnicianDate,
        user_agent: typeof window == 'undefined' ? '' : window.navigator.userAgent
      }, run_timestamp)

      recording.setFrame({
        start_location: this.debugPlace(start_location),
        end_location: end_location ? this.debugPlace(end_location) : null,
        max_tour_minutes: this.changedTourContainer.max_tour_minutes,
        now_day_ms: ctx.now_day_ms,
        configured_start_ms: ctx.configured_start_ms,
        is_arrival_time: ctx.is_arrival_time,
        driving_time_factor: ctx.driving_time_factor,
        operation_time_filter: PARegistry.filterControl.selectedOperationTimeFilter,
        automatic_lunch_break: this.automaticLunchBreakCalculation,
        lunch_break_after_operation_number: this.automaticLunchBreakCalculation ? null : this.lunchBreakAfterOperationNumber
      })

      this.debugRecording = recording
    } catch (error) {
      this.debugRecording = null
      console.warn('Debug-Aufzeichnung konnte nicht gestartet werden', error)
    }
  }

  private finishDebugRecording(run_timestamp: number, route_found: boolean, route: OptimizedRoute, fixed_lateness_ms: number): void {
    const recording = this.debugRecording
    // Nur der Lauf, der die Aufzeichnung angelegt hat, darf sie abschließen und entfernen — sonst
    // löscht ein überholter Lauf, der später fertig wird, die Aufzeichnung des nachfolgenden
    if (!recording || recording.runTimestamp != run_timestamp) return
    this.debugRecording = null

    // Ohne Optimierung (unveränderte Tour) gibt es nichts Auswertbares; die vorherige
    // Aufzeichnung dieses Technikertages bleibt dann erhalten
    if (!this.newTourTouched) return

    try {
      const snapshot = recording.finish({
        route_found: route_found,
        errors: route.errors || [],
        insertions: (route.insertions || []).map(insertion => ({
          operation_id: insertion.operation.id,
          ticket_id: insertion.operation.ticket.id,
          start_time_ms: insertion.start_time_ms,
          end_time_ms: insertion.end_time_ms
        })),
        unfitting_operation_ids: this.changedTourContainer.unfittingOperations.map(operation => operation.id),
        fixed_lateness_ms: fixed_lateness_ms
      }, Date.now())

      if (snapshot) TourDebugStore.save(snapshot)
    } catch (error) {
      console.warn('Debug-Aufzeichnung konnte nicht abgeschlossen werden', error)
    }
  }

  /** Registrierte Orte für die Auflösung der Orts-Schlüssel in getDrivingData */
  private optimizerLocations = new Map<string, PALocation>()

  private optimizerLocationKey(location: PALocation): string {
    const key = `${location.coordinates.latitude},${location.coordinates.longitude}`
    this.optimizerLocations.set(key, location)
    return key
  }

  private buildOptimizerContext(): TourOptimizerContext {
    const ts_now = Date.now()
    const day_is_today = this.day.utc_timestamp < ts_now && ts_now < this.day.utc_timestamp + 24 * 60 * 60 * 1000
    // Die Aufzeichnung wird hier fest gebunden statt bei jedem Abruf neu gelesen: Ein abgebrochener
    // Lauf, dessen Fahrzeit-Anfragen noch offen sind, schreibt sonst in die Aufzeichnung des
    // nachfolgenden Laufs
    const recording = this.debugRecording
    return {
      now_day_ms: day_is_today ? ts_now - PARegistry.timeControl.dateToTimestamp(new Date(), true, false) : null,
      configured_start_ms: PARegistry.settingsControl.getWorkStartMsForTechnician(this.technician.id),
      is_arrival_time: PARegistry.settingsControl.getIsArrivalTimeForTechnician(this.technician.id),
      driving_time_factor: this.technician.drivingTimeFactor,
      getDrivingData: async (from_id, to_id, use_cache) => {
        const from_location = this.optimizerLocations.get(from_id)
        const to_location = this.optimizerLocations.get(to_id)
        const route = await from_location.getDistanceToLocation(to_location, use_cache)
        recording?.addDistance(from_id, to_id, use_cache, route.duration, route.distance)
        return { duration: route.duration, distance: route.distance }
      },
      getLunchBreakConfig: tour_minutes => PATour.getLunchBreakTimeConfig(tour_minutes),
    }
  }

  private async toOptimizerOperation(operation: PAOperation, work_time_ms: number, opening_weekday: WeekDay): Promise<TourOptimizerOperation> {
    const ticket = operation.ticket
    const location = ticket.location
    const coords = location.coordinates
    const invalid_coordinates = NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES.find(c => c.lng == coords.longitude && c.lat == coords.latitude)
    const invalid_project = NO_LOCATION_CHANGE_FOR_PROJECTS.includes(ticket.project.id)

    const insertion_config = this.insertionConfigMap.get(operation.id)

    let started_end_time_ms: number
    let started_drive_and_work_ms: number
    if (operation.date_travel_start) {
      const time_data = await operation.getOperationTimeData(this.technician, 0)
      const use_planned = PARegistry.filterControl.selectedOperationTimeFilter == 'planned'
      const worktime_minutes = use_planned ? time_data.estimated_worktime : time_data.average_worktime
      started_end_time_ms = (use_planned ? time_data.estimated_end_time : time_data.average_end_time) * 60 * 1000
      started_drive_and_work_ms = (time_data.driving_time + worktime_minutes) * 60 * 1000
    }

    const datesla = ticket.datesla
    let same_day_sla_ms: number = null
    if (datesla) {
      const sla_day_timestamp = PARegistry.timeControl.dateToTimestamp(new Date(datesla), true, true)
      if (sla_day_timestamp === this.day.utc_timestamp) {
        same_day_sla_ms = PARegistry.timeControl.dateTimeToDayMinutes(datesla) * 60 * 1000
      }
    }

    const opening_time_string = ticket.store_openings[opening_weekday]?.open

    const core_operation: TourOptimizerOperation = {
      id: operation.id,
      ticket_id: ticket.id,
      date_fixed: !!operation.date_fixed,
      has_started: !!operation.date_travel_start,
      operation_day_ms: operation.operation_date ? PARegistry.timeControl.dateTimeToDayMinutes(operation.operation_date) * 60 * 1000 : 0,
      work_time_ms: work_time_ms,
      triggers_location_change: ticket.triggersLocationChange(),
      location_id: this.optimizerLocationKey(location),
      is_location_anchor: !invalid_coordinates && !invalid_project,
      city: ticket.address_city || null,
      tour_index: insertion_config ? insertion_config.tour_index : -1,
      removable: insertion_config ? insertion_config.removable : false,
      started_end_time_ms: started_end_time_ms,
      started_drive_and_work_ms: started_drive_and_work_ms,
      same_day_sla_ms: same_day_sla_ms,
      sla_end_limit_ms: datesla ? new Date(datesla).getTime() - this.day.local_timestamp : null,
      sla_info: datesla ? ticket.slaDateInformation() : null,
      opening_day_ms: opening_time_string ? PARegistry.timeControl.timeStringToMinutes(opening_time_string) * 60 * 1000 : null,
    }

    // Rohwerte zusätzlich festhalten: Nur im Vergleich beider Seiten ist erkennbar, ob ein Fehler
    // schon in dieser Umrechnung entstanden ist oder erst in der Zeitrechnung des Kerns
    if (this.debugRecording) {
      const store = operation.getStore()
      const raw: TourDebugRawOperation = {
        operation_id: operation.id,
        ticket_id: ticket.id,
        external_order_nr: ticket.external_order_nr || null,
        store_name: store ? store.address_company : null,
        address: [ticket.address_street, ticket.address_street_no].filter(part => part).join(' ') || null,
        zip_code: ticket.address_zip || null,
        city: ticket.address_city || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        operation_date: operation.operation_date || null,
        date_fixed: !!operation.date_fixed,
        date_travel_start: operation.date_travel_start || null,
        date_on_site: operation.date_on_site || null,
        date_finished: operation.date_finished || null,
        datesla: datesla || null,
        priority_id: ticket.priority ? ticket.priority.id : null,
        priority_name: ticket.priority ? ticket.priority.name : null,
        time_estimation_minutes: ticket.time_estimation ?? null,
        project_id: ticket.project ? ticket.project.id : null,
        opening_time: opening_time_string || null
      }
      this.debugRecording.addOperation(raw, core_operation)
    }

    return core_operation
  }

  /** Score einer Routen-Permutation (Distanz/Dauer) über den puren Kern berechnen */
  private async scoreRoute(route: PAOperation[], start_location: PALocation, end_location: PALocation | null): Promise<{ distance: number, duration: number }> {
    const light_ops = route.map(op => ({
      triggers_location_change: op.ticket.triggersLocationChange(),
      location_id: this.optimizerLocationKey(op.ticket.client.location),
    }))
    return await TourOptimizer.routeScore(
      light_ops,
      this.optimizerLocationKey(start_location),
      end_location ? this.optimizerLocationKey(end_location) : null,
      this.buildOptimizerContext()
    )
  }

  /**
   * Prüft ob eine Route ins Zeitbudget passt und entfernt ggf. Aufträge (Kern-Delegation).
   * Reihenfolge-Detail: Die Arbeitszeiten hängen über die Prioritäts-Zähler von der
   * Gesamtreihenfolge ab — deshalb wird erst interleavt und dann die Zeiten berechnet.
   */
  async findTimeConstrainedPartialRoute(
    route_data: {
      start_location: PALocation;
      route: PAOperation[];
      end_location: PALocation;
      extra_operations_without_location_change: PAOperation[],
      fixed_date_operations_without_location_change: PAOperation[]
    },
    max_tour_time_ms: number,
    highest_remove_count: number
  ): Promise<{ partial_route: PAOperation[]; removed_operations: PAOperation[], fixed_date_operations_without_location_change: PAOperation[], fixed_lateness_ms: number } | null> {

    const ctx = this.buildOptimizerContext()
    const opening_weekday = PARegistry.timeControl.getTimeStampsWeekDay(Date.now())

    // Fixtermine ohne Ortswechsel nach Uhrzeit sortieren
    const fixed_date_ops_no_location_pa = [...route_data.fixed_date_operations_without_location_change].sort((op_a, op_b) => {
      return new Date(op_a.operation_date).getTime() - new Date(op_b.operation_date).getTime()
    })

    const pa_by_id = new Map<number, PAOperation>()
    const core_by_id = new Map<number, TourOptimizerOperation>()
    const convert = async (ops: PAOperation[]): Promise<TourOptimizerOperation[]> => {
      const res: TourOptimizerOperation[] = []
      for (const op of ops) {
        // Arbeitszeit zunächst 0 — sie wird nach dem Interleaving in Gesamtreihenfolge nachgetragen
        const core_op = await this.toOptimizerOperation(op, 0, opening_weekday)
        pa_by_id.set(op.id, op)
        core_by_id.set(op.id, core_op)
        res.push(core_op)
      }
      return res
    }
    const core_route = await convert(route_data.route)
    const core_extras = await convert(route_data.extra_operations_without_location_change)
    const core_fixed_no_loc = await convert(fixed_date_ops_no_location_pa)

    const route_with_extras = TourOptimizer.buildRouteWithExtraOperations(core_route, core_extras)

    // Arbeitszeiten in der (reihenfolge-sensitiven) Gesamtreihenfolge berechnen
    const all_operations = route_with_extras.concat(core_fixed_no_loc).map(core_op => pa_by_id.get(core_op.id))
    const priority_idxs = await this.technician.currentTourPlannings.getRoutesPriorityOperationCountsOnDay(all_operations, this.day.utc_timestamp)
    for (const operation of all_operations) {
      const priority_entry = priority_idxs.find(route_idxs => route_idxs.operation == operation)
      core_by_id.get(operation.id).work_time_ms = operation.calculateOperationTimeMilliseconds({
        use_technician: this.technician,
        use_priority_idx: priority_entry ? priority_entry.count : 0
      })
    }

    const start_location_id = this.optimizerLocationKey(route_data.start_location)
    const end_location_id = route_data.end_location ? this.optimizerLocationKey(route_data.end_location) : null

    const result = await TourOptimizer.findTimeConstrainedPartialRoute(
      route_with_extras,
      core_fixed_no_loc,
      start_location_id,
      end_location_id,
      max_tour_time_ms,
      highest_remove_count,
      ctx
    )

    // Kern-Aufruf mitschneiden: Weil der Kern deterministisch ist und die Fahrzeiten in der
    // Aufzeichnung liegen, lässt sich genau dieser Aufruf lokal als Regressionstest wiederholen
    this.debugRecording?.addCoreCall({
      operation_ids: route_with_extras.map(core_op => core_op.id),
      fixed_no_location_operation_ids: core_fixed_no_loc.map(core_op => core_op.id),
      start_location_id: start_location_id,
      end_location_id: end_location_id,
      max_tour_time_ms: max_tour_time_ms,
      highest_remove_count: highest_remove_count,
      work_time_ms_by_operation_id: route_with_extras.concat(core_fixed_no_loc).reduce((times, core_op) => {
        times[core_op.id] = core_op.work_time_ms
        return times
      }, {} as Record<number, number>),
      result: result ? {
        partial_route_operation_ids: result.partial_route.map(core_op => core_op.id),
        removed_operation_ids: result.removed_operations.map(core_op => core_op.id),
        fixed_lateness_ms: result.fixed_lateness_ms
      } : null
    })

    if (!result) return null

    return {
      partial_route: result.partial_route.map(op => pa_by_id.get(op.id)),
      removed_operations: result.removed_operations.map(op => pa_by_id.get(op.id)),
      fixed_date_operations_without_location_change: result.fixed_date_operations_without_location_change.map(op => pa_by_id.get(op.id)),
      fixed_lateness_ms: result.fixed_lateness_ms
    }
  }

  /** Baut aus den bewerteten Routen die konkrete Tour (Kern-Delegation, siehe computeOptimizedRoute) */
  async getOptimizedRoute(scored_routes: ScoredRoute[], config: {start_location: PALocation, end_location?: PALocation}): Promise<{ optimized_route: OptimizedRoute, used_scored_route: ScoredRoute }> {

    const ctx = this.buildOptimizerContext()
    const opening_weekday = PARegistry.timeControl.getTimeStampsWeekDay(Date.now())
    const pa_by_id = new Map<number, PAOperation>()
    const original_by_core = new Map<TourOptimizerScoredRoute, ScoredRoute>()

    const core_scored_routes: TourOptimizerScoredRoute[] = []
    for (const scored_route of (scored_routes || [])) {
      const route_priority_idxs = await this.technician.currentTourPlannings.getRoutesPriorityOperationCountsOnDay(scored_route.route, this.day.utc_timestamp)

      const core_route: TourOptimizerOperation[] = []
      for (const operation of scored_route.route) {
        const priority_entry = route_priority_idxs.find(route_idxs => route_idxs.operation == operation)
        const work_time_ms = operation.calculateOperationTimeMilliseconds({
          use_technician: this.technician,
          use_priority_idx: priority_entry ? priority_entry.count : 0
        })
        pa_by_id.set(operation.id, operation)
        core_route.push(await this.toOptimizerOperation(operation, work_time_ms, opening_weekday))
      }

      const core_extras: TourOptimizerOperation[] = []
      for (const operation of scored_route.sorted_extra_operations_with_appointment_date) {
        // Termin-Extra-Aufträge rechnen wie bisher ohne Prioritäts-Index
        const work_time_ms = operation.calculateOperationTimeMilliseconds({ use_technician: this.technician })
        pa_by_id.set(operation.id, operation)
        core_extras.push(await this.toOptimizerOperation(operation, work_time_ms, opening_weekday))
      }

      const core_scored_route: TourOptimizerScoredRoute = {
        route: core_route,
        score: scored_route.score,
        removed_count: scored_route.removed_operations.length,
        fixed_lateness_ms: scored_route.fixed_lateness_ms || 0,
        sorted_extra_operations_with_appointment_date: core_extras
      }
      original_by_core.set(core_scored_route, scored_route)
      core_scored_routes.push(core_scored_route)
    }

    const start_location = config.start_location
    const { optimized_route, used_scored_route, reset_manual_lunch_break } = await TourOptimizer.computeOptimizedRoute(core_scored_routes, {
      start_location_id: this.optimizerLocationKey(start_location),
      start_location_city: start_location.city || null,
      end_location_id: config.end_location ? this.optimizerLocationKey(config.end_location) : null,
      lunch_break_after_operation_number: this.lunchBreakAfterOperationNumber || null,
      ctx: ctx
    })

    if (reset_manual_lunch_break) {
      this.automaticLunchBreakCalculation = true
      this.lunchBreakAfterOperationNumber = null
    }

    if (!optimized_route) {
      return { optimized_route: null, used_scored_route: null }
    }

    return {
      optimized_route: {
        errors: optimized_route.errors,
        insertions: optimized_route.insertions.map(ins => {
          return {
            operation: pa_by_id.get(ins.operation.id),
            start_time_ms: ins.start_time_ms,
            tour_time_ms: ins.tour_time_ms,
            end_time_ms: ins.end_time_ms
          }
        }),
        lunch_break: optimized_route.lunch_break
      },
      used_scored_route: original_by_core.get(used_scored_route) || null
    }
  }

  public updateInsertionConfigMap(insertion_operations: PAOperation[]) {
    let all_operations: PAOperation[]

    const tour_operations = this.tour.operations
    if (tour_operations) {
      for (const operation of tour_operations) {
        if (!this.insertionConfigMap.has(operation.id)) {
          const tour_idx = operation.hasStarted() ? tour_operations.indexOf(operation) : -1
          this.insertionConfigMap.set(operation.id, { removable: false, tour_index: tour_idx })
        } else if (operation.date_travel_start && this.insertionConfigMap.get(operation.id).tour_index == -1) {
          // Auftrag ist gestartet, während der Planer offen war: absoluten Index nachziehen,
          // damit die belegte Position im Nummer-Select korrekt gesperrt wird
          const existing_config = this.insertionConfigMap.get(operation.id)
          existing_config.removable = false
          existing_config.tour_index = tour_operations.indexOf(operation)
        }
      }
    }

    for (const operation of insertion_operations) {
      if (!this.insertionConfigMap.has(operation.id)) {
        const is_main_operation_to_plan = PARegistry.tourPlannerControl.tourPlannerPlanningMode == 'operation' && PARegistry.tourPlannerControl.operationToPlanID == operation.id
        this.insertionConfigMap.set(operation.id, {removable: !is_main_operation_to_plan, tour_index: -1})
      }
    }

    all_operations = tour_operations.concat(insertion_operations)
    for (const key of this.insertionConfigMap.keys()) {
      if (!all_operations.find(op => key == op.id)) {
        this.insertionConfigMap.delete(key)
      }
    }
  }

  removeOperationsFromNewTour(remove_operations_ids: number[]): void {
    const insert_operations = this.newTourConfig?.insert_operations || []
    const remove_insert_operations = insert_operations.filter(ins_op => remove_operations_ids.find(rem_op_id => rem_op_id == ins_op.id))
    const delete_operations = this.tour.operations.filter(
      tour_op => !tour_op.hasStarted() && remove_operations_ids.find(rem_op_id => rem_op_id == tour_op.id)
    )
    if (remove_insert_operations.length) {
      const timestamps = PARegistry.tourPlannerControl.tourPlannerPlanningMode == 'technician' ? [this.day.utc_timestamp] : null
      PARegistry.tourPlannerControl.planningInstructions.removeOperationsTechnicianConstraints(remove_insert_operations, [this.technician], timestamps)
      PARegistry.tourPlannerControl.removeExtraOperationsIfNotUsed(remove_insert_operations)
    }
    if (delete_operations.length) {
      const changes_before = this.newTourConfig
      this.updateNewTourConfig({
        delete_operations: (changes_before.delete_operations || []).concat(delete_operations)
      })
    }
  }

  restoreOperationToNewTour(add_operation: PAOperation): void {
    const changes_before = this.newTourConfig
    const deleted_operations_before = changes_before?.delete_operations || []
    const deleted_operation = deleted_operations_before.find(operation => operation.id == add_operation.id)
    if (deleted_operation) {
      this.updateNewTourConfig({
        delete_operations: deleted_operations_before.filter(op => op.id != add_operation.id)
      })
    }
  }

  insertionConfigIndexIsDisabled(idx: number, exclude_operation_id?: number): boolean {
    // Vor bzw. an der Position eines heute bereits verstrichenen Fixtermins kann nichts mehr
    // eingeplant werden — diese Positionen sind gesperrt
    if (idx <= this.lastTodayPassedFixedOperationIndex()) return true
    // exclude_operation_id: Die eigene Operation blockiert ihren aktuell gewählten Index nicht —
    // sonst wäre die eigene Position im Nummer-Select immer ausgegraut
    return !![...this.insertionConfigMap].find(i_config_kv => i_config_kv[0] != exclude_operation_id && !this.changedTourContainer.unfittingOperations.find(op => op.id == i_config_kv[0]) && i_config_kv[1].tour_index == idx)
  }

  // Absolute Position des letzten Fixtermins der (neuen) Tour, dessen Uhrzeit heute bereits
  // verstrichen ist — -1, wenn es keinen gibt oder der Tag nicht heute ist
  private lastTodayPassedFixedOperationIndex(): number {
    const ts_now = Date.now()
    if (!(this.day.utc_timestamp < ts_now && ts_now < this.day.utc_timestamp + 24 * 60 * 60 * 1000)) return -1
    const now_day_ms = ts_now - PARegistry.timeControl.dateToTimestamp(new Date(), true, false)
    let last_passed_idx = -1
    this.changedTourContainer.tour.operations.forEach((operation, idx) => {
      if (operation.date_fixed && PARegistry.timeControl.dateTimeToDayMinutes(operation.operation_date) * 60 * 1000 < now_day_ms) {
        last_passed_idx = idx
      }
    })
    return last_passed_idx
  }

  updateNewTourConfig(config?: NewTourConfig): void {
    if (!this.isPastTechnicianDate) {
      const delete_operations_before = this.newTourConfig?.delete_operations || []
      const insert_operations_before = this.newTourConfig?.insert_operations || []
      const custom_start_before = this.newTourConfig?.custom_start_location || null
      const custom_end_before = this.newTourConfig?.custom_end_location || null

      this._newTourConfig = {
        force_new_tour: config?.force_new_tour,
        insert_operations: config?.insert_operations || insert_operations_before,
        delete_operations: config?.delete_operations || delete_operations_before,
        custom_start_location: typeof config?.custom_start_location == 'undefined' ? custom_start_before : config.custom_start_location,
        custom_end_location: typeof config?.custom_end_location == 'undefined' ?  custom_end_before : config.custom_end_location,
      }

      this.updateNewTour(this._newTourConfig).then(
        _ => {
          //this.selectTimelineTourForMap(changes.skip_map_center)
        }
      ).catch(
        // Fehler dürfen nicht als unbehandelte Rejection verschwinden — Planung würde sonst stumm abbrechen
        error => console.error('Fehler bei der Tour-Neuberechnung:', error)
      )
    }
  }

  async checkCurrentTourForDBChanges(): Promise<DBOperationChange[]> {
    return await this.checkTourForDBChanges(this.tour)
  }

  async checkNewTourForDBChanges(): Promise<DBOperationChange[]> {
    return await this.checkTourForDBChanges(this.changedTourContainer.tour)
  }

  async checkTourForDBChanges(tour: PATour): Promise<DBOperationChange[]> {
    const new_tour_operations = tour.operations
    const check_operations = new_tour_operations.filter(op => op.id > 0)

    if (!check_operations.length) return []

    return await new Promise<DBOperationChange[]>(
      resolve => {
        PAApiServices.planningAssistantService.getPlanningAssistantOperations(check_operations.map(op => op.id)).subscribe(
          data => {
            const res: DBOperationChange[] = []
            for (const operation_hash of data) {
              const new_tour_base_operation = PARegistry.dataControl.getOperation(operation_hash.id, null)
              const user_ids_changed: OperationUIDChanged = new_tour_base_operation.user_ids[0] != operation_hash.user_ids[0] ? {current_uid: new_tour_base_operation.user_ids[0], db_uid: operation_hash.user_ids[0]} : null
              const operation_date_changed: OperationDateChanged = new_tour_base_operation.operation_date != operation_hash.operation_date ? {current_operation_date: new_tour_base_operation.operation_date, db_operation_date: operation_hash.operation_date} : null
              if (user_ids_changed || operation_date_changed) {
                if (!user_ids_changed) {
                  res.push({base_operation: new_tour_base_operation, date_changed: operation_date_changed})
                } else if (!operation_date_changed) {
                  res.push({base_operation: new_tour_base_operation, uid_changed: user_ids_changed})
                } else {
                  res.push({base_operation: new_tour_base_operation, uid_changed: user_ids_changed, date_changed: operation_date_changed})
                }
              }
            }
            resolve(res)
          },
          err => {
            console.log(err)
          }
        )
      }
    )
  }

  getConstrainedOperations(operations: PAOperation[], planning_instructions: PAPlanningInstructions): PAOperation[] {
    return operations.filter(op => planning_instructions.operationIsDoableOnTechnicianDate(op, this))
  }

  closestDistanceToLocation(ref_location: PALocation) {
    let closest_location_distance = -1
    for (let location of [this.getStartLocation(), ...this.tour.operations.map(op => op.ticket.location), this.getEndLocation()]) {
      location = location || this.tour.technicianDate.technician.location
      const distance = PAUtil.calcDistanceAsTheCrowFlies(location.coordinates.latitude, location.coordinates.longitude, ref_location.coordinates.latitude, ref_location.coordinates.longitude)
      if (closest_location_distance == -1 || closest_location_distance > distance) {
        closest_location_distance = distance
      }
    }
    return closest_location_distance
  }

  async setNewAbsences() {
    this.absences = await this.technician.getAbsencesForTimestamp(this.day.utc_timestamp)
  }

  getWorkloadOrAbsenceClass(): string {
    const day_ts = this.day.utc_timestamp
    const holidays = this.technician.getLocalPublicHolidays(day_ts)
    if (holidays.length || this.absences.length || this.noShift) {
      return 'red'
    } else {
      const workload_percent = this.tour.route.time_specific_data[PARegistry.filterControl.selectedOperationTimeFilter].workload_percent
      if (25 > workload_percent) {
        return 'green'
      } else if (75 > workload_percent) {
        return 'yellow'
      } else {
        return 'red'
      }
    }
  }

  getTargetWorkMinutes(): number {
    const day_ts = this.day.utc_timestamp
    const weekday = this.day.week_day
    const holidays = this.technician.getLocalPublicHolidays(day_ts)

    if (holidays.length || this.absences.length || this.noShift) { return  0 }

    const absence_user = this.technician.getAbsenceUser()

    if (absence_user) {
      return absence_user.getSummedShiftMinutesOnWeekdayWithFallback(weekday, day_ts)
    } else {
      if (PARegistry.timeControl.workDays.includes(weekday)) {
        return 480
      } else {
        return 0
      }
    }
  }

  async executeOnLoaded(): Promise<void> {
    this.loadingStatus = 'loaded'
    await this.setNewAbsences()
  }
}

export interface ChangedTourContainer {
  tour: PATour
  max_tour_minutes: number;
  unfittingOperations: PAOperation[]
  deletingOperations: PAOperation[]
}

export interface OptimizedRoute {
  errors: string[],
  insertions: Insertion[],
  lunch_break?: { after_operation_idx: number, duration_minutes: number }
}

interface ScoredRoute {
  route: PAOperation[];
  score: {
    distance: number;
    duration: number;
  };
  sorted_extra_operations_with_appointment_date: PAOperation[]
  removed_operations: PAOperation[]
  /** Summierte Fixtermin-Verspätung der Permutation in ms (Sortierschlüssel nach removed) */
  fixed_lateness_ms: number
}

export interface DBOperationChange {
  base_operation: PAOperation
  uid_changed?: OperationUIDChanged
  date_changed?: OperationDateChanged
}

export interface OperationUIDChanged {
  current_uid: number
  db_uid: number
}

export interface OperationDateChanged {
  current_operation_date: string
  db_operation_date: string
}
